// ============================================================
// shared/geometry.js — brut, volume fini, volume de copeau.
// ------------------------------------------------------------
// À partir des cotes de la PIÈCE FINIE (lues sur le plan par
// l'agent 1, ou saisies), calcule le BRUT à acheter et les
// volumes. 100 % déterministe, aucun LLM.
//
// Une seule implémentation, importée par l'API et par le front.
// Chez AGC, `geometry.service.js` et `data.jsx` coexistaient et
// avaient déjà divergé : `cylinder_box` n'existait que d'un côté.
//
// Les marges ne sont PAS des constantes : elles viennent de
// `app_settings.pricing`. Un atelier n'a pas les mêmes habitudes
// qu'un autre.
// ============================================================

import { num, isProvided } from './pricing.js';

/** Valeurs de repli, alignées sur `app_settings.pricing` de `CLAUDE.md` §5. */
/**
 * Les SURÉPAISSEURS de brut. Rien à voir avec la marge commerciale —
 * qui n'existe plus depuis le RDV du 5 août. Le mot « margin » est ici
 * celui de l'usinage : la matière qu'on laisse pour pouvoir usiner.
 *
 * Elles doivent devenir réglables PAR FORME (§3.6), et le cas « une
 * pièce à tenir en mors demande 5 mm de plus » n'est pas un réglage
 * global : c'est une règle métier que Charles doit pouvoir écrire.
 */
export const GEOMETRY_DEFAULTS = {
  raw_section_margin_pct: 15,
  raw_step_mm: 10,
  raw_depth_margin_pct: 10,
};

const roundTo = (x, step) => Math.round((num(x) / step + Number.EPSILON)) * step;
const round1 = (x) => roundTo(x, 0.1);

/** Arrondi au palier commercial supérieur (10 mm par défaut). */
function ceilToStep(value, step) {
  const s = num(step, GEOMETRY_DEFAULTS.raw_step_mm);
  if (!(s > 0)) return round1(value);
  return Math.ceil((num(value) - Number.EPSILON) / s) * s;
}

/**
 * Arrondi d'une profondeur à une épaisseur réellement achetable.
 * Si la liste des épaisseurs de stock est fournie (issue de
 * `material_formats`), on prend la première qui convient. Sinon on retombe
 * sur le palier commercial — jamais sur un `round1` qui produirait des
 * cotes comme 12,1 mm, ce que faisait AGC.
 */
export function snapDepth(value, { stockThicknesses, step } = {}) {
  const v = num(value);
  if (Array.isArray(stockThicknesses) && stockThicknesses.length > 0) {
    const sorted = stockThicknesses.map((t) => num(t)).filter((t) => t > 0).sort((a, b) => a - b);
    const found = sorted.find((t) => t >= v - 1e-9);
    if (found !== undefined) return found;
    // plus épais que tout le stock connu : on remonte au palier, et l'appelant
    // verra que la cote dépasse le catalogue.
  }
  return ceilToStep(v, step);
}

/**
 * Enveloppe de la pièce finie.
 * @returns {{shape:'cylinder', diameter:number, length:number}
 *          |{shape:'box', l:number, w:number, h:number}
 *          |null}
 */
export function partEnvelope(geom) {
  if (!geom || !geom.partShape || !geom.part) return null;
  const p = geom.part;
  if (geom.partShape === 'cylinder') {
    const length = isProvided(p.length) ? num(p.length) : num(p.h);
    return { shape: 'cylinder', diameter: num(p.diameter), length };
  }
  return { shape: 'box', l: num(p.l), w: num(p.w), h: num(p.h) };
}

/** Volume d'une enveloppe, en cm³. */
export function envelopeVolume(env) {
  if (!env) return 0;
  if (env.shape === 'cylinder') {
    return (Math.PI * (env.diameter / 2) ** 2 * env.length) / 1000;
  }
  return (env.l * env.w * env.h) / 1000;
}

/**
 * Volume retiré par une feature (perçage, poche, lamage…), en cm³.
 * `diameter` → cylindrique ; `l` × `w` → prismatique. `count` multiplie.
 */
/**
 * Le volume enlevé par UN usinage, en cm³.
 *
 * ── Le défaut qui a motivé cette réécriture ──
 *
 * Un usinage TRAVERSANT n'a pas de profondeur écrite sur le plan : elle
 * vaut l'épaisseur de la pièce, et c'est évident pour un mécanicien.
 * Le code lisait `depth` à `null`, le prenait pour 0, et l'usinage
 * n'enlevait RIEN. Sur la rondelle SABCA, l'alésage Ø41,5 traversant
 * fait 30 % de la pièce : le volume fini sortait à 30,39 cm³ au lieu de
 * ~21, et les copeaux à 50 % au lieu de 65 %.
 *
 * Ce n'était pas visible : 30,39 est un nombre plausible, et le ratio
 * d'enlèvement entre dans la signature qui sert à comparer les pièces.
 *
 * @param {object} f          l'usinage
 * @param {number} epaisseur  l'épaisseur traversée, en mm
 * @returns {{volume: number, connu: boolean}} `connu: false` = aucune
 *   règle pour ce type. On le SIGNALE, on ne l'ignore pas.
 */
/**
 * À partir de combien de diamètres d'alésage traversant distincts on
 * soupçonne un alésage étagé compté plusieurs fois.
 *
 * Mesuré sur les 38 pièces de la base le 7 septembre 2026 : 0 à 3
 * partout, 4 ou 5 sur les trois paliers. Le seuil est posé À 4, donc
 * au-dessus de tout ce que le corpus produit de sain.
 */
export const SEUIL_ALESAGES_ETAGES = 4;

export function featureVolume(f, epaisseur = 0) {
  if (!f) return { volume: 0, connu: true };
  const n = Math.max(1, Math.round(num(f.count, 1)));
  const d = num(f.diameter);
  const d2 = num(f.diameter2);
  const e = num(epaisseur);

  // Traversant : la profondeur EST l'épaisseur. Borgne : sa profondeur.
  const prof = f.traversant ? e : num(f.depth);
  const cyl = (diam, h) => (Math.PI * (diam / 2) ** 2 * h * n) / 1000;

  switch (f.kind) {
    case 'percage':
    case 'alesage':
    case 'taraudage':
      // Un taraudage se compte au Ø de PERÇAGE : le filet n'enlève que
      // la matière du profil, négligeable devant le trou lui-même.
      return { volume: d > 0 && prof > 0 ? cyl(d, prof) : 0, connu: true };

    case 'percage_etage':
      // Deux cylindres superposés : le petit sur toute la traversée, le
      // gros sur sa propre profondeur.
      return {
        volume:
          (d > 0 && prof > 0 ? cyl(d, prof) : 0) +
          (d2 > 0 && num(f.depth) > 0 ? cyl(d2, num(f.depth)) - cyl(d, num(f.depth)) : 0),
        connu: true,
      };

    case 'lamage':
      return { volume: d2 > 0 && num(f.depth) > 0 ? cyl(d2, num(f.depth)) : 0, connu: true };

    case 'fraisurage': {
      // Tronc de cône entre le Ø de perçage et le Ø de fraisure. La
      // hauteur se déduit de l'angle quand il est donné — un fraisurage
      // à 90° descend de (D−d)/2.
      if (!(d2 > d && d > 0)) return { volume: 0, connu: true };
      const h = num(f.depth) > 0 ? num(f.depth) : (d2 - d) / 2;
      const r1 = d / 2;
      const r2 = d2 / 2;
      return {
        volume: ((Math.PI * h * (r1 * r1 + r1 * r2 + r2 * r2)) / 3 - Math.PI * r1 * r1 * h) * n / 1000,
        connu: true,
      };
    }

    case 'rainure':
    case 'fente':
      return {
        volume:
          num(f.l) > 0 && num(f.w) > 0 && num(f.depth) > 0
            ? (num(f.l) * num(f.w) * num(f.depth) * n) / 1000
            : 0,
        connu: true,
      };

    case 'poche':
      return {
        volume:
          num(f.l) > 0 && num(f.w) > 0 && num(f.depth) > 0
            ? (num(f.l) * num(f.w) * num(f.depth) * n) / 1000
            : 0,
        connu: true,
      };

    case 'gorge': {
      // Une couronne : le Ø extérieur moins le fond, sur la largeur.
      const larg = num(f.w) > 0 ? num(f.w) : num(f.depth);
      if (!(d > 0 && d2 > 0 && larg > 0)) return { volume: 0, connu: true };
      const grand = Math.max(d, d2);
      const petit = Math.min(d, d2);
      return {
        volume: (Math.PI * ((grand / 2) ** 2 - (petit / 2) ** 2) * larg * n) / 1000,
        connu: true,
      };
    }

    case 'chanfrein':
      // EXPLICITEMENT à zéro : négligeable devant l'erreur de
      // l'approximation. Le dire vaut mieux que l'omettre — un type
      // oublié et un type négligé se ressemblent dans le code, pas dans
      // le raisonnement.
      return { volume: 0, connu: true };

    case 'surfacage':
      // Un surfaçage enlève de la SURÉPAISSEUR de brut, pas de la
      // matière de la pièce finie. Il est déjà compté dans l'écart
      // brut/fini : le compter ici serait un double comptage.
      return { volume: 0, connu: true };

    default:
      // Un type sans règle est SIGNALÉ, jamais ignoré en silence.
      return { volume: 0, connu: false };
  }
}

/**
 * Volume fini = enveloppe − usinages, en cm³.
 *
 * `null` si aucune géométrie exploitable — surtout pas 0, qui serait
 * indiscernable d'une pièce réellement vide.
 *
 * @returns {{volume: number|null, inconnus: string[], alerte: string|null,
 *   doute: string|null}}
 */
export function finishedVolumeDetail(geom) {
  const env = partEnvelope(geom);
  if (!env) return { volume: null, inconnus: [], alerte: null, doute: null };

  const brut = envelopeVolume(env);
  // L'épaisseur traversée : la longueur d'un cylindre, la hauteur d'un
  // parallélépipède. C'est elle que prend un usinage débouchant.
  const epaisseur = env.shape === 'cylinder' ? num(env.length) : num(env.h);

  const inconnus = [];
  let enleve = 0;
  for (const f of geom.features ?? []) {
    const r = featureVolume(f, epaisseur);
    enleve += r.volume;
    if (!r.connu && f.kind) inconnus.push(f.kind);
  }

  const volume = brut - enleve;

  // ══════════════════════════════════════════════════════════════
  // L'ALÉSAGE ÉTAGÉ COMPTÉ AUTANT DE FOIS QU'IL A DE DIAMÈTRES
  // ──────────────────────────────────────────────────────────────
  // Signalé en réunion le 1er septembre 2026 : la masse finie du
  // « Palier - Bearing » (devis 2026-08-070) sort à 37,8 kg là où la
  // pièce réelle en pèse 66. Mesuré : l'agent a relevé Ø72, Ø80, Ø90,
  // 2 × Ø91 et Ø122, tous « traversants », tous dans la MÊME coupe A-A
  // — c'est UN alésage étagé, et le calcul l'a soustrait CINQ fois, sur
  // toute la hauteur. 5 421 cm³ des 5 441 cm³ retirés viennent de là.
  //
  // ── Pourquoi on SIGNALE au lieu de corriger ──
  //
  // Rien dans les faits relevés ne dit que ces alésages sont coaxiaux :
  // l'agent ne rapporte aucune position. Les fusionner serait DEVINER,
  // et deviner produirait un autre nombre faux, sans que rien ne le
  // dise. On nomme donc le doute et on laisse la valeur : le volume
  // fini est une DÉDUCTION, et une déduction se propose avec sa
  // provenance (§2bis), elle ne se refuse pas.
  //
  // ── Pourquoi ce critère et pas un ratio d'enlèvement ──
  //
  // Le ratio a été mesuré sur les 38 pièces de la base : des pièces
  // parfaitement saines enlèvent 65 %, 79 %, 86 % de leur enveloppe —
  // ce sont de petites pièces tournées dans la barre. Un seuil sur le
  // ratio aurait crié sur elles, et c'est le défaut exact du détecteur
  // au pixel : *un contrôle qui se déclenche sur cinq témoins sains sur
  // cinq ne détecte rien.*
  //
  // Le nombre de DIAMÈTRES d'alésage traversant distincts, lui,
  // discrimine : mesuré sur les mêmes 38 pièces, il vaut 0 à 3 partout,
  // et 4 ou 5 sur exactement TROIS pièces — les trois paliers, c'est-à-
  // dire précisément la famille dont l'alésage est étagé.
  const diametresTraversants = [...new Set(
    (geom.features ?? [])
      .filter((f) => f?.kind === 'alesage' && f?.traversant === true && num(f.diameter) > 0)
      .map((f) => num(f.diameter)),
  )].sort((a, b) => b - a);

  let doute = null;
  if (diametresTraversants.length >= SEUIL_ALESAGES_ETAGES) {
    doute =
      `${diametresTraversants.length} alésages traversants de diamètres différents ont été relevés `
      + `(Ø${diametresTraversants.join(', Ø')}) sans position. S'il s'agit d'un même alésage ÉTAGÉ, `
      + 'il est compté une fois par diamètre et la masse de la pièce finie est sous-estimée. '
      + 'À vérifier sur le plan.';
  }

  // ── Les garde-fous. Une violation se SIGNALE, elle ne se corrige pas
  //    en silence : un volume rabattu à zéro aurait l'air d'un résultat.
  let alerte = null;
  if (volume <= 0) {
    alerte =
      `Les usinages relevés enlèvent ${enleve.toFixed(2)} cm³ sur une enveloppe de ` +
      `${brut.toFixed(2)} cm³ : le volume fini serait nul ou négatif. Une cote ou un ` +
      'usinage est mal relevé.';
  } else if (volume > brut) {
    alerte = 'Le volume fini dépasse son enveloppe : une cote est incohérente.';
  }

  return {
    volume: alerte ? null : volume,
    inconnus: [...new Set(inconnus)],
    alerte,
    doute,
  };
}

/** Compatibilité : le volume seul. */
export function finishedVolumeOf(geom) {
  return finishedVolumeDetail(geom).volume;
}

/**
 * Calcule le brut à acheter à partir de la pièce finie.
 *
 * Section : majorée de `raw_section_margin_pct`, puis arrondie au palier
 * supérieur de `raw_step_mm`.
 *   - brut rond contenant un prismatique  → Ø ≥ diagonale de la section
 *   - brut carré contenant un rond        → côté ≥ Ø
 * Profondeur : majorée de `raw_depth_margin_pct`, puis calée sur une
 * épaisseur réellement achetable.
 *
 * @param {Object} geom  géométrie de la pièce finie
 * @param {'box'|'cylinder'|'cylinder_box'} brutShape  forme de brut retenue
 * @param {Object} [options]
 * @param {Object} [options.settings]  `app_settings.pricing`
 * @param {number[]} [options.stockThicknesses]  épaisseurs de stock connues
 * @returns {{raw_shape:string, raw_l:number, raw_w:number, raw_h:number,
 *            raw_diameter:number, raw_length:number, warnings:Array}|null}
 */
export function computeBrut(geom, brutShape = 'barreau', options = {}) {
  const env = partEnvelope(geom);
  if (!env) return null;

  const s = { ...GEOMETRY_DEFAULTS, ...(options.settings ?? {}) };
  const sectionFactor = 1 + num(s.raw_section_margin_pct) / 100;
  const depthFactor = 1 + num(s.raw_depth_margin_pct) / 100;
  const step = num(s.raw_step_mm, GEOMETRY_DEFAULTS.raw_step_mm);
  const warnings = [];

  const depth = env.shape === 'cylinder' ? env.length : env.h;
  if (!(depth > 0)) {
    warnings.push({ field: 'part.depth', level: 'error', message: 'Profondeur de la pièce absente.' });
  }
  const rawDepth = snapDepth(depth * depthFactor, {
    stockThicknesses: options.stockThicknesses,
    step,
  });

  if (brutShape === 'barre_ronde' || brutShape === 'barre_ronde_prismatique') {
    // Rond : le Ø doit contenir la pièce. Si la pièce est prismatique,
    // c'est sa diagonale qui commande, pas son plus grand côté.
    const need = env.shape === 'cylinder' ? env.diameter : Math.hypot(env.l, env.w);
    if (!(need > 0)) {
      warnings.push({ field: 'part.section', level: 'error', message: 'Section de la pièce absente.' });
    }
    return {
      raw_shape: brutShape,
      raw_diameter: ceilToStep(need * sectionFactor, step),
      raw_length: rawDepth,
      raw_l: 0,
      raw_w: 0,
      raw_h: 0,
      warnings,
    };
  }

  // Prismatique : côté carré contenant la pièce. Si la pièce est ronde,
  // le côté doit contenir le Ø.
  const need = env.shape === 'box' ? Math.max(env.l, env.w) : env.diameter;
  if (!(need > 0)) {
    warnings.push({ field: 'part.section', level: 'error', message: 'Section de la pièce absente.' });
  }
  const side = ceilToStep(need * sectionFactor, step);
  return {
    raw_shape: 'barreau',
    raw_l: side,
    raw_w: side,
    raw_h: rawDepth,
    raw_diameter: 0,
    raw_length: 0,
    warnings,
  };
}

/**
 * Volume de copeau = brut − fini, en cm³. `null` si l'un des deux manque.
 * Sert à mesurer la matière réellement enlevée, donc le temps d'usinage.
 */
export function chipVolume(rawVolumeCm3Value, finishedVolumeCm3) {
  if (!isProvided(rawVolumeCm3Value) || !isProvided(finishedVolumeCm3)) return null;
  return Math.max(0, num(rawVolumeCm3Value) - num(finishedVolumeCm3));
}
